import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ApiError, type ApiToken } from "../lib/api";
import { ButtonSpinner, Skeleton } from "./Loading";

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; tokens: ApiToken[] }
  | { kind: "error" };

// Owner's personal API tokens. A token lets a script (the avista-clarity
// Claude plugin, a CI job) call the owner API with
// `Authorization: Bearer <token>` instead of a 30-day session cookie.
// The secret is shown once, straight after creation; the list only ever
// carries names and timestamps. Revoked rows stay visible as an audit view.

export function ApiTokensPanel() {
  const [load, setLoad] = useState<LoadState>({ kind: "loading" });
  const [created, setCreated] = useState<{ name: string; secret: string } | null>(null);

  const reload = useCallback(async () => {
    try {
      const res = await api.listApiTokens();
      setLoad({ kind: "ready", tokens: res.tokens });
    } catch {
      setLoad({ kind: "error" });
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  return (
    <div className="mt-10">
      <h2 className="text-lg font-semibold">Your API tokens</h2>
      <p className="mt-1 text-xs text-neutral-500">
        Let scripts such as the Clarity Claude plugin call the API as you, without
        signing in every month. Send one as{" "}
        <code>Authorization: Bearer &lt;token&gt;</code>. Tokens don’t expire; revoke
        any you no longer use.
      </p>

      <CreateTokenForm
        onCreated={(result) => {
          setCreated(result);
          void reload();
        }}
      />

      {created ? (
        <NewTokenReveal
          name={created.name}
          secret={created.secret}
          onDone={() => setCreated(null)}
        />
      ) : null}

      <div className="mt-4 overflow-hidden rounded-lg border border-neutral-200 bg-white">
        {load.kind === "loading" ? (
          <div className="space-y-3 px-5 py-4">
            <Skeleton className="h-3 w-48" />
            <Skeleton className="h-3 w-40" />
          </div>
        ) : load.kind === "error" ? (
          <p className="px-5 py-4 text-sm text-red-600">Couldn’t load tokens.</p>
        ) : load.tokens.length === 0 ? (
          <p className="px-5 py-4 text-sm text-neutral-500">No tokens yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-5 py-3 font-medium">Name</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 font-medium">Last used</th>
                <th className="px-5 py-3 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {load.tokens.map((token) => (
                <TokenRow key={token.id} token={token} onChanged={reload} />
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CreateTokenForm({
  onCreated,
}: {
  onCreated: (result: { name: string; secret: string }) => void;
}) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const trimmed = name.trim();
    if (busy || !trimmed) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createApiToken({ name: trimmed });
      setName("");
      onCreated({ name: res.token.name, secret: res.secret });
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 409
          ? "You’ve reached the token limit. Revoke one first."
          : "Couldn’t create a token. Try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="mt-4 flex flex-wrap items-center gap-2">
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Token name, e.g. Laptop plugin"
        aria-label="Token name"
        maxLength={80}
        disabled={busy}
        className="w-full max-w-xs rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm placeholder:text-neutral-400 focus:border-neutral-900 focus:outline-none"
      />
      <button
        type="submit"
        disabled={busy || !name.trim()}
        className="rounded-md bg-neutral-900 px-4 py-2 text-sm text-white hover:bg-neutral-700 disabled:opacity-50"
      >
        {busy ? <ButtonSpinner label="Creating" /> : "Create token"}
      </button>
      {error ? <p className="w-full text-xs text-red-600">{error}</p> : null}
    </form>
  );
}

function NewTokenReveal({
  name,
  secret,
  onDone,
}: {
  name: string;
  secret: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the value is selectable in the field.
    }
  }

  return (
    <div className="mt-4 rounded-lg border border-neutral-300 bg-neutral-50 p-4">
      <p className="text-sm font-medium text-neutral-900">“{name}” is ready</p>
      <p className="mt-1 text-xs text-neutral-600">
        Copy it now. This is the only time it’s shown. For the Claude plugin, put it
        in <code>~/.claude/.env</code> as <code>CLARITY_TOKEN</code>.
      </p>
      <div className="mt-3 flex items-center gap-2">
        <input
          readOnly
          value={secret}
          aria-label="New API token"
          onFocus={(e) => e.currentTarget.select()}
          className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 font-mono text-xs text-neutral-900"
        />
        <button
          type="button"
          onClick={() => void copy()}
          className="shrink-0 rounded-md border border-neutral-300 bg-white px-3 py-2 text-xs hover:bg-neutral-100"
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="shrink-0 px-2 py-2 text-xs text-neutral-500 hover:text-neutral-900"
        >
          Done
        </button>
      </div>
    </div>
  );
}

function TokenRow({
  token,
  onChanged,
}: {
  token: ApiToken;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoked = token.revokedAt !== null;

  async function onRevoke() {
    if (
      !window.confirm(
        `Revoke “${token.name}”? Anything using it will stop working immediately.`,
      )
    )
      return;
    setBusy(true);
    setError(null);
    try {
      await api.revokeApiToken(token.id);
      await onChanged();
    } catch {
      setError("Couldn’t revoke. Try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <tr>
      <td className="px-5 py-3 align-middle">
        <span
          className={
            revoked ? "text-neutral-400 line-through" : "font-medium text-neutral-900"
          }
        >
          {token.name}
        </span>
        {error ? <p className="mt-1 text-xs text-red-600">{error}</p> : null}
      </td>
      <td className="px-5 py-3 align-middle text-neutral-700">
        {new Date(token.createdAt).toLocaleDateString()}
      </td>
      <td className="px-5 py-3 align-middle text-neutral-700">
        {token.lastUsedAt ? new Date(token.lastUsedAt).toLocaleString() : "Never"}
      </td>
      <td className="px-5 py-3 text-right align-middle">
        {revoked ? (
          <span className="text-xs text-neutral-400">
            Revoked {new Date(token.revokedAt!).toLocaleDateString()}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => void onRevoke()}
            disabled={busy}
            className="text-xs text-neutral-500 hover:text-red-600 disabled:opacity-50"
          >
            {busy ? <ButtonSpinner label="Revoking" /> : "Revoke"}
          </button>
        )}
      </td>
    </tr>
  );
}
